/**
 * Sentence splitting for transcript display.
 *
 * The proxy streams the transcript as segments, not sentences, and the panes render those
 * segments back-to-back inline — so a long turn arrives as one unbroken wall of text
 * ("いらっしゃる 。まあ 、どういうのも…" running for a whole paragraph). Splitting on sentence
 * terminators at render time puts one sentence per line without changing a single character of
 * the transcript itself.
 *
 * A sentence can also straddle two segments, so callers pair `splitSentences` (breaks WITHIN a
 * segment) with `endsSentence` (whether the NEXT segment should start on a fresh line).
 */

/** Always end a sentence — these characters do not occur mid-token in any language we handle. */
const CJK_TERMINATORS = new Set(['。', '！', '？', '…', '．'])
/** End a sentence only when whitespace or the end of the segment follows — see `splitSentences`. */
const LATIN_TERMINATORS = new Set(['.', '!', '?'])
/** Closing punctuation that belongs to the sentence it follows, so the break goes after it. */
const CLOSERS = new Set(['」', '』', '）', ')', ']', '】', '"', "'", '”', '’', '»'])

/** Advances past any closing punctuation trailing the terminator at `index`. */
function endOfSentence(text: string, index: number): number {
  let end = index + 1
  while (end < text.length && CLOSERS.has(text[end])) end += 1
  return end
}

/** Whether the period at `index` belongs to a dotted abbreviation such as `v.v.` or `e.g.`. */
function isDottedAbbreviation(text: string, index: number): boolean {
  const token = text.slice(0, index + 1).match(/[A-Za-z.]+$/)?.[0] ?? ''
  return /^(?:[A-Za-z]\.){2,}$/.test(token)
}

/**
 * Splits one segment into sentences, preserving every character (including the spacing the
 * backend's `format_segment` adds).
 *
 * A Latin `.` / `!` / `?` only breaks when whitespace or the segment end follows: without that
 * guard "gemini-3.5", "v.v." and "10.5%" would each be torn across lines. CJK terminators break
 * unconditionally. Returns the original text as a single line when nothing splits, so a caller
 * never has to handle an empty result.
 */
export function splitSentences(text: string): string[] {
  const sentences: string[] = []
  let start = 0

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i]
    const isCjk = CJK_TERMINATORS.has(char)
    const isLatin = LATIN_TERMINATORS.has(char)
    if (!isCjk && !isLatin) continue

    const end = endOfSentence(text, i)
    if (isLatin && end < text.length && !/\s/.test(text[end])) continue
    if (char === '.' && isDottedAbbreviation(text, i)) continue

    sentences.push(text.slice(start, end))
    start = end
    i = end - 1
  }

  if (start < text.length) sentences.push(text.slice(start))

  // Drop fragments that are only the spacing between sentences — they would render as blank
  // lines. If that leaves nothing (a segment of pure whitespace), keep the text as-is so the
  // space between two words is never silently swallowed.
  const kept = sentences.filter((sentence) => sentence.trim() !== '')
  return kept.length > 0 ? kept : [text]
}

/** Whether `text` closes a sentence, i.e. the next segment should start on a new line. */
export function endsSentence(text: string): boolean {
  const trimmed = text.trimEnd()
  if (!trimmed) return false

  let index = trimmed.length - 1
  while (index >= 0 && CLOSERS.has(trimmed[index])) index -= 1
  if (index < 0) return false

  const char = trimmed[index]
  // At the end of a segment there is nothing after the terminator, so the whitespace rule that
  // protects "3.5" mid-text cannot apply — a trailing "." really is a sentence end.
  return CJK_TERMINATORS.has(char) || LATIN_TERMINATORS.has(char)
}
